import { observer } from 'mobx-react-lite'

import Stack from '@mui/material/Stack'

import { H2, InstitutionLogo, Text } from '@mxenabled/mxui'

import {
  useAccountStore,
  INCOME_ACCOUNT_TYPES,
  PAYMENT_ACCOUNT_TYPES,
  SPENDING_ACCOUNT_TYPES,
  useAccountDisplayName,
  useGlobalCopyStore,
  useAppConfig,
} from '../../../../common'
import { useAccountUiStore } from '../../../hooks/useUiStore'
import { getAccountBalanceDetails } from '../../../utils/AccountFields'
import { getChartLabels } from '../../../utils/AccountDetails'

import AccountDetailsChart from './AccountDetailsChart'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

interface AccountDetailsHeaderProps {
  ChartComponent?: React.ComponentType
  customDetailsChart?: React.ReactElement
}

const AccountDetailsHeader = ({
  customDetailsChart,
  ChartComponent,
}: AccountDetailsHeaderProps) => {
  const { institutions } = useAccountStore()
  const { selectedAccount: account } = useAccountUiStore()
  const { config } = useAppConfig()
  const { accounts: accountsCopy } = useGlobalCopyStore()
  const { firstSubtitle, firstValue, hasAvailableBalance, secondSubtitle, secondValue } =
    getAccountBalanceDetails(account, accountsCopy, config.display_available_balance_in_accounts)

  const institution = institutions.find(
    (institution) => institution.guid === account?.institution_guid,
  )

  const showChart = [
    ...INCOME_ACCOUNT_TYPES,
    ...PAYMENT_ACCOUNT_TYPES,
    ...SPENDING_ACCOUNT_TYPES,
  ].includes(account.account_type)

  const displayName = useAccountDisplayName(account)

  const { title, description } = getChartLabels(account.account_type, accountsCopy)

  return (
    <Stack bgcolor="background.default" p={24}>
      <Stack gap={12}>
        <InstitutionLogo
          alt={accountsCopy.institution_logo_alt}
          institutionGuid={account.institution_guid || ''}
          size={64}
        />
        <Text variant="body2">{institution?.name}</Text>
      </Stack>
      <Stack gap={8} mb={24} mt={4}>
        <H2>{displayName}</H2>
        <Stack direction="row" justifyContent="space-between">
          <Text bold={hasAvailableBalance} variant="body1">
            {firstSubtitle}
          </Text>
          <Text bold={true} variant="body1">
            {formatCurrency(firstValue, '0,0.00')}
          </Text>
        </Stack>
        <Stack direction="row" justifyContent="space-between">
          <Text variant="body2">{secondSubtitle}</Text>
          <Text variant="body2">{secondValue}</Text>
        </Stack>
      </Stack>
      {showChart && (
        <Stack aria-label={description} role="img">
          {customDetailsChart ? (
            customDetailsChart
          ) : (
            <AccountDetailsChart ChartComponent={ChartComponent} header={title} />
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default observer(AccountDetailsHeader)
